import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import * as Sentry from "@sentry/react";
import { SentryLink } from "apollo-link-sentry";
import { type AuthOptions, createAuthLink } from "aws-appsync-auth-link";
import { AUTH_TYPE } from "aws-appsync-auth-link/lib/auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import { useEffect, useMemo, useState } from "react";
import { Config } from "services/configuration/eatSmartConfig";
import { handleSendApolloErrorToSentry } from "services/external/sentry";
import { getFridgeId } from "./browser/localStorage";
import { resolveRetryAttempt } from "./retryConnectionPolicy";

export const CACHE_SHAPE = {
	typePolicies: {
		Fridge: {
			fields: {
				dishes: {
					merge: true,
				},
			},
		},
		DishType: {
			fields: {
				dishes: {
					merge: false,
				},
			},
		},
		DishTypeSubscription: {
			fields: {
				dishes: {
					merge: false,
				},
			},
		},
	},
};

export const useFridgeApolloClient = () => {
	const [isConnected, setIsConnected] = useState(true);
	const [isSubscriptionBroken, setIsSubscriptionBroken] = useState(false);

	/* One client per app lifetime: recreating it discards the cache and re-handshakes
	the AppSync WS, restarting every Apollo hook in the tree (this was the
	handleSetInitialData refire bug). The useState setters captured below are
	identity-stable, and Config is static module config, so the memo runs once. */
	const client = useMemo(() => {
		const { aws, connectionSettings } = Config;
		const { appsyncUrl: url, region } = aws;
		const { connectionTimeout } = connectionSettings;
		const auth: AuthOptions = {
			type: AUTH_TYPE.AWS_LAMBDA,
			token: aws.apiKey,
		};

		const errorLink = onError((error) => {
			handleSendApolloErrorToSentry(error);
		});

		const link = ApolloLink.from([
			errorLink,
			new RetryLink({
				// Connection-state policy (flip offline on the first subscription error, retry for
				// recovery, backstop at the attempt limit) lives in resolveRetryAttempt so it is unit-tested.
				attempts: (count, operation, error) =>
					resolveRetryAttempt(count, operation.operationName, error, {
						onSubscriptionBroken: () => setIsSubscriptionBroken(true),
						onDisconnected: () => setIsConnected(false),
					}),
				delay: {
					initial: 500,
					max: 2000,
				},
			}),
			new SentryLink({
				attachBreadcrumbs: {
					includeError: true,
					includeFetchResult: true,
					includeQuery: true,
					includeVariables: true,
				},
			}),
			createAuthLink({ url: url, region: region, auth: auth }),
			createSubscriptionHandshakeLink(
				{
					url: url,
					region: region,
					auth: auth,
					keepAliveTimeoutMs: connectionTimeout,
				},
				new HttpLink({ uri: url }),
			),
		]);

		return new ApolloClient({
			link: link,
			cache: new InMemoryCache({ ...CACHE_SHAPE }),
		});
	}, []);

	useEffect(() => {
		if (isConnected) {
			return;
		}
		const ping = setInterval(async () => {
			await fetch("https://dns.google", { mode: "no-cors" })
				.then(() => {
					setIsConnected(true);
				})
				.catch((error: unknown) => {
					Sentry.withScope((scope) => {
						scope.setExtra("fridgeId", getFridgeId());
						Sentry.captureException(
							new Error("Could not ping Google DNS - Tablet is offline", {
								cause: error,
							}),
						);
					});
				});
		}, 5000);
		// The cleanup owns the clear: it runs both on unmount and when isConnected flips back to
		// true (the setIsConnected above re-runs this effect), so the interval can never leak.
		return () => clearInterval(ping);
	}, [isConnected]);

	return {
		client: client,
		isConnected: isConnected,
		setIsConnected: setIsConnected,
		isSubscriptionBroken: isSubscriptionBroken,
		setIsSubscriptionBroken: setIsSubscriptionBroken,
	};
};
